import { FF_DOWNLOAD_TREATMENT_LETTER } from '@/constants/featureFlags';
import { usePrescriptionsTracking } from '@/hooks/analytics/usePrescriptionsTracking';
import { useCreateConfirmationOfTreatmentLetter } from '@/hooks/patient/useCreateConfirmationOfTreatmentLetter';
import { usePatientClientCode } from '@/hooks/patient/usePatientClientCode';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { openFileFromAsyncUrl } from '@/utils/openFileFromAsyncUrl';
import { CircularProgress, Stack, Tooltip, Typography, toast } from '@montugroup/design-system';
import { MgPrescription } from '@montugroup/icons';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { SvgIcon, useTheme } from '@mui/material';
import type { ScriptMetadata } from './common';
import {
  StyledExpiryRow,
  StyledHeader,
  StyledHeaderContent,
  StyledHeaderMain,
  StyledIconContainer,
  StyledMetaRow,
  StyledScriptRow,
  StyledTreatmentButton,
} from './PrescriptionHeader.styles';

export interface PrescriptionHeaderProps {
  prescriptionCode: string;
  prescriptionMetadata: ScriptMetadata;
}

export const DOWNLOAD_TREATMENT_LETTER_LABEL = 'Download Evidence of Prescription';

export const PrescriptionHeader = ({ prescriptionCode, prescriptionMetadata }: PrescriptionHeaderProps) => {
  const { flags } = useFeatureFlags();
  const theme = useTheme();
  const { mutateAsync, isPending } = useCreateConfirmationOfTreatmentLetter();
  const { trackDownloadTreatmentLetter } = usePrescriptionsTracking();

  const isDownloadTreatmentLetterEnabled = flags[FF_DOWNLOAD_TREATMENT_LETTER];

  const { data: clientCode, isLoading: isClientCodeLoading } = usePatientClientCode({
    enabled: isDownloadTreatmentLetterEnabled,
  });

  const handleDownloadTreatmentClick = async () => {
    if (isDownloadTreatmentLetterDisabled) {
      return;
    }

    try {
      if (!clientCode) {
        throw new Error('Missing clientCode');
      }

      trackDownloadTreatmentLetter(prescriptionCode);
      await openFileFromAsyncUrl(async () => {
        const { preSignedUrl } = await mutateAsync(clientCode, prescriptionCode);
        return preSignedUrl;
      });
    } catch {
      toast.error('Oops, failed to download evidence of prescription.');
    }
  };

  const isDownloadTreatmentLetterDisabled = isClientCodeLoading || isPending;

  const showExpiryWarning =
    prescriptionMetadata.daysUntilExpiry <= 30 &&
    prescriptionMetadata.daysUntilExpiry >= 1 &&
    !prescriptionMetadata.inActive &&
    prescriptionMetadata.status === 'Active';

  return (
    <StyledHeader>
      <StyledHeaderMain>
        <StyledIconContainer>
          <SvgIcon component={MgPrescription} color="primary" />
        </StyledIconContainer>

        <StyledHeaderContent>
          <StyledScriptRow>
            <Stack direction="row" gap={theme.spacing(2)}>
              <SvgIcon component={MgPrescription} color="primary" sx={{ display: { sm: 'none' } }} />

              <StyledMetaRow py={{ sm: theme.spacing(1) }}>
                <Typography variant="body1">Script ID</Typography>
                <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                  {prescriptionCode}
                </Typography>
              </StyledMetaRow>
            </Stack>

            {isDownloadTreatmentLetterEnabled && (
              <StyledTreatmentButton
                onClick={handleDownloadTreatmentClick}
                variant="outlined"
                size="small"
                disabled={isDownloadTreatmentLetterDisabled}
                startIcon={isPending ? <CircularProgress size={18} /> : <DownloadIcon />}
                sx={{ display: { xs: 'none', sm: 'flex', lg: 'none' } }}
              >
                {DOWNLOAD_TREATMENT_LETTER_LABEL}
              </StyledTreatmentButton>
            )}
          </StyledScriptRow>

          <Stack direction={{ sm: 'row' }} gap={{ xs: theme.spacing(3), sm: theme.spacing(4) }}>
            <StyledMetaRow>
              <Typography variant="body1" sx={{ minWidth: { xs: theme.spacing(16), sm: 'auto' } }}>
                Created
              </Typography>
              <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                {prescriptionMetadata.createdDate}
              </Typography>
            </StyledMetaRow>

            <StyledMetaRow>
              <Typography variant="body1" sx={{ minWidth: { xs: theme.spacing(16), sm: 'auto' } }}>
                Expiry
              </Typography>

              <StyledExpiryRow
                sx={{ color: showExpiryWarning ? theme.palette.warning.main : 'inherit' }}
                data-testid="expiry-warning"
              >
                <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium} sx={{ color: 'inherit' }}>
                  {prescriptionMetadata.expiryDate}
                </Typography>

                {showExpiryWarning && (
                  <Tooltip title={`Expires in ${prescriptionMetadata.daysUntilExpiry} days`} arrow>
                    <InfoOutlined fontSize="inherit" color="inherit" data-testid="expiry-info-icon" />
                  </Tooltip>
                )}
              </StyledExpiryRow>
            </StyledMetaRow>
          </Stack>
        </StyledHeaderContent>
      </StyledHeaderMain>

      {isDownloadTreatmentLetterEnabled && (
        <>
          {/* Outlined variant with medium size on mobile: text variant looks odd, spacing is awkward when stacked, and medium size improves tap target. */}
          <StyledTreatmentButton
            sx={{ display: { xs: 'flex', sm: 'none' } }}
            onClick={handleDownloadTreatmentClick}
            variant="outlined"
            size="medium"
            disabled={isDownloadTreatmentLetterDisabled}
            startIcon={isPending ? <CircularProgress size={18} /> : <DownloadIcon />}
          >
            {DOWNLOAD_TREATMENT_LETTER_LABEL}
          </StyledTreatmentButton>
          {/* Hidden on sm-md to avoid cramped spacing when button is vertically centered. */}
          <StyledTreatmentButton
            sx={{ display: { xs: 'none', lg: 'flex' } }}
            onClick={handleDownloadTreatmentClick}
            variant="text"
            size="small"
            disabled={isDownloadTreatmentLetterDisabled}
            startIcon={isPending ? <CircularProgress size={18} /> : <DownloadIcon />}
          >
            {DOWNLOAD_TREATMENT_LETTER_LABEL}
          </StyledTreatmentButton>
        </>
      )}
    </StyledHeader>
  );
};
